import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, VideoIcon } from "lucide-react";
import { useRtcSession } from "@/components/telemed/useRtcSession";

const STATUS_TEXT = {
  init: "Preparando câmera e microfone…",
  waiting: "Aguardando o outro participante entrar…",
  connecting: "Conectando…",
  connected: "Conectado",
  denied: "Permita o acesso à câmera e ao microfone para iniciar.",
};

export const VideoCall = ({ roomId, displayName, role = "", peerLabel = "Participante", onEnd, onRemoteStream }) => {
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const { status, localStream, remoteStream } = useRtcSession({ roomId, displayName, role, onRemoteStream });
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const remoteOn = !!remoteStream && status === "connected";

  useEffect(() => { if (localRef.current) localRef.current.srcObject = localStream; }, [localStream]);
  useEffect(() => { if (remoteRef.current) remoteRef.current.srcObject = remoteStream; }, [remoteStream]);

  const toggleMic = () => {
    const t = localStream?.getAudioTracks?.()[0];
    if (t) { t.enabled = !t.enabled; setMic(t.enabled); }
  };
  const toggleCam = () => {
    const t = localStream?.getVideoTracks?.()[0];
    if (t) { t.enabled = !t.enabled; setCam(t.enabled); }
  };
  const hangup = () => onEnd?.();

  const initials = (displayName || "Você").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-slate-900 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="rtc-videocall">
      <div className="relative flex aspect-video items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
        <video ref={remoteRef} data-testid="rtc-remote" autoPlay playsInline className={`h-full w-full object-cover ${remoteOn ? "" : "hidden"}`} />
        {!remoteOn && (
          <div className="text-center text-slate-300" data-testid="rtc-status" data-status={status}>
            {["init", "connecting"].includes(status) && <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-400" />}
            {status === "waiting" && (
              <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-slate-700 text-xl font-bold">
                <VideoIcon className="h-7 w-7 text-slate-300" />
              </div>
            )}
            <p className="mt-3 max-w-xs px-4 text-sm font-semibold">{STATUS_TEXT[status] || ""}</p>
            {status === "waiting" && <p className="mt-1 text-xs text-slate-500">Compartilhe o link com {peerLabel.toLowerCase()}</p>}
          </div>
        )}
        <div className="absolute bottom-3 right-3 h-24 w-32 overflow-hidden rounded-lg border border-white/10 bg-slate-800">
          <video ref={localRef} data-testid="rtc-local" autoPlay playsInline muted className={`h-full w-full object-cover ${cam ? "" : "hidden"}`} />
          {!cam && (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-slate-200">{initials}</div>
            </div>
          )}
          <span className="absolute bottom-1 left-1 rounded bg-black/40 px-1 text-[9px] text-white">Você</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 bg-slate-950/60 p-3">
        <button data-testid="rtc-mic-btn" onClick={toggleMic} className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${mic ? "bg-slate-700 text-white hover:bg-slate-600" : "bg-rose-600 text-white"}`}>
          {mic ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
        </button>
        <button data-testid="rtc-cam-btn" onClick={toggleCam} className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${cam ? "bg-slate-700 text-white hover:bg-slate-600" : "bg-rose-600 text-white"}`}>
          {cam ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
        </button>
        <button data-testid="rtc-end-btn" onClick={hangup} className="flex h-10 items-center gap-1.5 rounded-full bg-rose-600 px-4 text-sm font-bold text-white hover:bg-rose-700">
          <PhoneOff className="h-4 w-4" /> Encerrar
        </button>
      </div>
    </div>
  );
};
